import {
  ORDER_STATUS_TO_SPEND_BUCKET,
  SHIPPING_METHOD_NOTE_PREFIX,
  computeOrderTotal as computeOrderTotalMoney,
  formatOrderRef,
  formatOwnAccountShippingLine,
} from '@misumi/shared'
import type { ChipTone } from '../../components/ui/Chip'
import type { components } from '../../lib/api/schema.gen'

export { formatOrderRef }

export type OrderStatus = components['schemas']['OrderStatus']
export type OrderLineStatus = components['schemas']['OrderLineStatus']
export type OrderSummary = components['schemas']['OrderSummary']
export type Order = components['schemas']['Order']
export type OrderLine = components['schemas']['OrderLine']
/** UID-411 — `OrderLine` widened with the real per-part thumbnail `GET /orders/{orderId}`
 * actually returns (`OrderDetail.lines: OrderLineWithThumbnail[]`). Kept SEPARATE from the
 * bare `OrderLine`/`Order` aliases above (rather than widening them directly) so the ~15
 * existing consumers of those two types — most of which never touch `thumbnailUrl` — are
 * untouched; only the order-detail page and its two thumbnail-consuming panels use this. */
export type OrderLineWithThumbnail = components['schemas']['OrderLineWithThumbnail']
/** The actual shape `GET /orders/{orderId}` returns: `Order` with `lines` widened to
 * {@link OrderLineWithThumbnail}. Structurally assignable to `Order` (extra field on
 * `lines`), so it satisfies every OTHER order-detail component still typed `order: Order`
 * (ActionBar, ActivityLogPanel, StatusStepper, OrderSummaryCard) with no changes there. */
export type OrderDetailData = Omit<Order, 'lines'> & {
  lines: OrderLineWithThumbnail[]
}
export type ActivityEntry = components['schemas']['ActivityEntry']
export type CustomsInfo = components['schemas']['CustomsInfo']
export type CustomsLineInfo = components['schemas']['CustomsLineInfo']
export type Shipment = components['schemas']['Shipment']
export type OrderDocument = components['schemas']['OrderDocument']

/** Canonical US-origin labels for the import distinction. `countryOfOrigin` is a free-form nullable
 * string, so case + punctuation are normalized ("U.S.", "us", "USA", "United States of America" all
 * read as domestic). A null/blank origin — every live-placed order today — is NOT an import. */
const US_ORIGIN_LABELS: ReadonlySet<string> = new Set([
  'us',
  'usa',
  'united states',
  'united states of america',
])

/** Normalize a free-form origin label for comparison: trim, lowercase, strip dots, collapse
 * internal whitespace so "U.S.", "us", and "United  States of America" all read as US. Called only
 * with a known non-blank origin (callers skip null/blank first). */
function isUsOrigin(countryOfOrigin: string): boolean {
  const normalized = countryOfOrigin.trim().toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ')
  return normalized.length === 0 || US_ORIGIN_LABELS.has(normalized)
}

/**
 * The shipping-terms state derived from the parts' country of ORIGIN (UID-232) — the single source
 * of truth shared by the summary card and the Customs tab. As of UID-232 EVERY order carries a
 * customs record, so record PRESENCE no longer signals import; origin does. THREE states, because
 * "origin not yet known" must NOT be conflated with "confirmed US":
 * - `import` — at least one line has a KNOWN, non-US origin → DDP, Fictiv is importer of record.
 * - `domestic` — origins are known and ALL US → DAP, the buyer incurs no customs.
 * - `undetermined` — no line has a known origin yet. This is every live-placed order today
 *   (placement writes null origins; the customer submit flow never sets origin, and Fictiv assigns
 *   it off-screen). DAP is the conservative default, but we must NOT claim US origin — the order
 *   may well be an import (the exact scenario this feature targets: an overseas order).
 */
export type CustomsOriginState = 'import' | 'domestic' | 'undetermined'

export function resolveCustomsOriginState(
  customs: CustomsInfo | null | undefined,
): CustomsOriginState {
  if (customs == null) {
    return 'undetermined'
  }
  let sawKnownOrigin = false
  for (const line of customs.lines ?? []) {
    const origin = line.countryOfOrigin
    if (origin == null || origin.trim().length === 0) {
      continue
    }
    if (!isUsOrigin(origin)) {
      return 'import'
    }
    sawKnownOrigin = true
  }
  return sawKnownOrigin ? 'domestic' : 'undetermined'
}

/** True only for a confirmed IMPORT (a known, non-US origin). Drives the summary card's DDP-vs-DAP
 * label and the Customs tab's importer-of-record framing. A `domestic` or `undetermined` order is
 * NOT an import (DAP; no importer of record). */
export function isImportCustoms(customs: CustomsInfo | null | undefined): boolean {
  return resolveCustomsOriginState(customs) === 'import'
}

/**
 * Pure client-side domain logic for the Orders list + Order detail screens
 * (UID-63, docs/design/uid-63-orders-ux-spec.md). Kept dependency-free (no
 * React) so the aggregation math, status→UI mapping, and filter predicates
 * are unit-testable without rendering anything — the ADR-0036 §5 "trivial
 * aggregates computed client-side" boundary lives here, not scattered across
 * components.
 */

// ---------------------------------------------------------------------------
// §1.6 — pipeline stat-card bucket mapping
// ---------------------------------------------------------------------------

export type PipelineBucket = 'in_production' | 'in_transit' | 'on_hold' | 'delivered'

/** Left-to-right stat-card order (UX spec §1.2 item 2). */
export const PIPELINE_BUCKETS: readonly PipelineBucket[] = [
  'in_production',
  'in_transit',
  'on_hold',
  'delivered',
]

export const PIPELINE_BUCKET_LABEL: Record<PipelineBucket, string> = {
  in_production: 'In production',
  in_transit: 'In transit',
  on_hold: 'On hold',
  delivered: 'Delivered',
}

/**
 * UX spec §1.6 table. `draft` and `cancelled` deliberately map to `undefined`
 * — excluded from all 4 pipeline buckets (draft orders aren't placed yet;
 * cancelled orders are excluded from the active pipeline but remain visible
 * in the table via a dedicated filter, §1.6 item 4). `placed` collapses into
 * "In production" (§1.6.1, recommendation: collapse for v1) and
 * `partially_shipped` collapses into "In transit" (§1.6.2) — both flagged to
 * PM as open questions in the spec, not re-litigated here.
 *
 * UID-95 O5 (verify-first, NO-OP): re-verified this taxonomy against
 * `Fictiv Platform.dc.html` directly rather than trusting the spec's own
 * paraphrase. Source evidence: `ordersList()` (~L5930) and `mkOrder()`
 * (~L6395, the function that appends a newly-placed order to
 * `runtimeOrders`) both only ever produce one of exactly FOUR status
 * strings — `'In production'`, `'Shipped'`, `'On hold'`, `'Delivered'` —
 * there is no `'placed'`, `'draft'`, or `'cancelled'` order status anywhere
 * in the prototype's data model; a freshly-placed order is stamped
 * `status:'In production'` directly (`mkOrder`), i.e. the prototype has
 * ALREADY folded "placed" into "In production" at the data-model level, not
 * as a display-only collapse. The 4-bucket `tileDef` (~L8075) and
 * `orderTotalCount=ordAll.length` (~L8081) both operate over that same
 * closed 4-status set — so "does the pipeline total exclude draft/
 * cancelled" is moot in the prototype (those statuses don't exist to
 * exclude). This build's behavior — folding `placed` into `in_production`,
 * excluding `draft`/`cancelled` from all 4 buckets while still showing them
 * in the table, and sharing one `ORDER_STATUS_TO_BUCKET` map between the
 * stat-card counts and the toolbar filter — is therefore ALREADY aligned
 * with the prototype's intent (this build's contract additionally models
 * `partially_shipped`/`closed`/`draft`/`cancelled`, which the prototype's
 * demo dataset never needed but which are real states this system must
 * support). Conclusion: NO CHANGE to `ORDER_STATUS_TO_BUCKET`,
 * `computePipelineTotals`, `computePipelineSummary`, or
 * `matchesToolbarFilter` — the existing unit tests below already exercise
 * this mapping and stay green as-is.
 *
 * UID-404 (ADR-0126 D3) — MOVED, not changed. The literal that used to sit
 * here now lives in `packages/shared/src/order-summary.ts` as
 * `ORDER_STATUS_TO_SPEND_BUCKET`, and this name is an alias of it. Every value
 * is byte-identical; nothing about the web behavior changed.
 *
 * WHY it moved: the api had its OWN copy of this mapping (a `switch` in
 * `order/spend.ts` with a `default: return null` arm) and the two had silently
 * DRIFTED — the api dropped `placed` and `closed`, so a tenant holding an order
 * in either state saw a pipeline tile count SHORT by that order while the
 * toolbar filter (this map) correctly included it. Clicking the tile therefore
 * revealed more rows than the tile claimed. THIS map won the reconciliation on
 * evidence: the UID-95 O5 verification recorded above, plus the existing tests.
 * One map now feeds both the api's tile counts and this module's filter, so
 * AC2's tile↔row agreement is a property of the construction rather than of a
 * convention — see the exhaustive agreement test in domain.test.ts.
 */
export const ORDER_STATUS_TO_BUCKET: Readonly<Record<OrderStatus, PipelineBucket | undefined>> =
  ORDER_STATUS_TO_SPEND_BUCKET

export type ToolbarFilter = 'all' | PipelineBucket | 'cancelled'

/**
 * Toolbar filter-chip order: matches the design handoff exactly (06-orders.png
 * + prototype `orderStatusChips`: All / In production / Shipped / Delivered /
 * On hold). The handoff mock shows only those 5 chips — there is no visible
 * "Cancelled" chip — but `cancelled` is real, working filter functionality
 * already wired in this codebase (ADR-0036 §5), so it is kept as a 6th chip
 * appended at the end rather than deleted to chase pixel-parity on a demo
 * account that happens to have no cancelled orders in the mock's dataset.
 */
export const TOOLBAR_FILTERS: readonly ToolbarFilter[] = [
  'all',
  'in_production',
  'in_transit',
  'delivered',
  'on_hold',
  'cancelled',
]

/**
 * Chip label text. Deliberately DIFFERENT from `PIPELINE_BUCKET_LABEL` for
 * `in_transit`: the handoff's stat card reads "In transit" but its toolbar
 * chip for the same bucket reads "Shipped" (prototype: tile label "In
 * transit" vs. status-chip raw value "Shipped") — not a typo, two different
 * labels for the same bucket depending on where it renders.
 */
export const TOOLBAR_FILTER_LABEL: Record<ToolbarFilter, string> = {
  all: 'All',
  in_production: 'In production',
  in_transit: 'Shipped',
  on_hold: 'On hold',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
}

export function matchesToolbarFilter(status: OrderStatus, filter: ToolbarFilter): boolean {
  if (filter === 'all') return true
  if (filter === 'cancelled') return status === 'cancelled'
  return ORDER_STATUS_TO_BUCKET[status] === filter
}

// ---------------------------------------------------------------------------
// Order-level status chip metadata (cross-screen consistency, UX spec §3 —
// identical taxonomy on the list table, the filter chips, and the detail
// stepper/per-line chips)
// ---------------------------------------------------------------------------

export interface StatusChipMeta {
  readonly label: string
  readonly tone: ChipTone
  /** Icon key resolved by the component layer (kept out of this pure module
   * so it stays React-free and independently testable). */
  readonly iconKey: IconKey
}

export type IconKey =
  | 'check-circle'
  | 'check-circle-filled'
  | 'setting'
  | 'truck'
  | 'pause-circle'
  | 'close-circle'
  | 'clock-circle'

/**
 * UID-5 (S9): re-derived directly from the prototype's per-order status pill
 * function, NOT from the earlier (build-only) blue/amber 2-tone scheme this
 * replaces. `Fictiv Platform.dc.html` ~L8010 defines exactly 3 row-status
 * pill colors for the order table (`P2`): `progress:pill('#F0811E',...)`
 * (orange — In production), `shipped:pill('#16BC9C',...)` (green — Shipped),
 * `hold:pill('#F54A4A',...)` (red/pink — On hold); ~L8163 additionally shows
 * `delivered` sharing the SAME green as `shipped`. Those 3 raw hexes are
 * exactly `--color-warning` / `--color-success` / `--color-error`
 * (tokens.css) — already-defined semantic tokens, not new colors — so this
 * mapping reuses `warning`/`success`/`error` tones rather than `info`.
 *
 * NOT unified with the pipeline stat-card swatches (`StatCard.tsx` /
 * `orders.css`'s `.stat-card-dot-*`) — that's a DIFFERENT, deliberately
 * divergent color treatment straight from the same prototype: its 4-tile
 * `tileDef` (`Fictiv Platform.dc.html` ~L8076) colors the "in transit"
 * tile BLUE (`--color-info`, `#4B7FFC`), while this row-chip mapping colors
 * the equivalent `shipped`/`partially_shipped` statuses GREEN (`success`).
 * Both are individually design-faithful for their own context (stat-card
 * tile vs. table row chip) — do NOT "DRY" them into one shared map; that
 * would regress one of the two to match the other and reintroduce a
 * fidelity gap.
 *
 * `placed` and `partially_shipped` have no distinct pill color in the
 * prototype's closed 3-status pill set (see the `ORDER_STATUS_TO_BUCKET` doc
 * comment above: the prototype's data model only ever produces
 * `in_production`/`shipped`/`on_hold`/`delivered`) — each is mapped to the
 * tone of the status it folds into elsewhere in this file (`placed`→
 * `in_production`'s `warning`; `partially_shipped`→`shipped`'s `success`,
 * since both represent "en route" the same way `ORDER_STATUS_TO_BUCKET`
 * groups them into the `in_transit` bucket) for internal consistency, not a
 * directly-observed design value.
 */
export const ORDER_STATUS_CHIP_META: Record<OrderStatus, StatusChipMeta> = {
  draft: { label: 'Draft', tone: 'neutral', iconKey: 'clock-circle' },
  placed: { label: 'Placed', tone: 'warning', iconKey: 'check-circle' },
  in_production: { label: 'In production', tone: 'warning', iconKey: 'setting' },
  partially_shipped: { label: 'Partially shipped', tone: 'success', iconKey: 'truck' },
  shipped: { label: 'Shipped', tone: 'success', iconKey: 'truck' },
  delivered: { label: 'Delivered', tone: 'success', iconKey: 'check-circle-filled' },
  closed: { label: 'Closed', tone: 'success', iconKey: 'check-circle-filled' },
  on_hold: { label: 'On hold', tone: 'error', iconKey: 'pause-circle' },
  cancelled: { label: 'Cancelled', tone: 'neutral', iconKey: 'close-circle' },
}

// ---------------------------------------------------------------------------
// §1.6 / §6 — client-side pipeline + spend aggregation over OrderSummary[]
// ---------------------------------------------------------------------------

export interface PipelineBucketTotals {
  readonly count: number
  readonly units: number
  readonly amount: number
}

export type PipelineTotals = Record<PipelineBucket, PipelineBucketTotals>

function emptyPipelineTotals(): PipelineTotals {
  return {
    in_production: { count: 0, units: 0, amount: 0 },
    in_transit: { count: 0, units: 0, amount: 0 },
    on_hold: { count: 0, units: 0, amount: 0 },
    delivered: { count: 0, units: 0, amount: 0 },
  }
}

/**
 * ADR-0036 §5 / UX spec §1.6: bucket orders by order-level `status` (not
 * line-level — this spec's assumption, flagged as an open question to PM),
 * summing `count`, `units` (`unitCount`), and `$` (`total.amount`) per
 * bucket. `draft`/`cancelled` orders are excluded entirely (never counted in
 * any bucket). Every bucket key is always present, zeroed when empty, so the
 * stat-card grid never has to guard against a missing key.
 */
export function computePipelineTotals(orders: readonly OrderSummary[]): PipelineTotals {
  const totals = emptyPipelineTotals()
  for (const order of orders) {
    const bucket = ORDER_STATUS_TO_BUCKET[order.status]
    if (!bucket) continue
    const bucketTotals = totals[bucket]
    totals[bucket] = {
      count: bucketTotals.count + 1,
      units: bucketTotals.units + order.unitCount,
      amount: bucketTotals.amount + Number(order.total.amount),
    }
  }
  return totals
}

/**
 * Order-pipeline card header summary ("N in flight · N total", 06-orders.png
 * top-right of the pipeline card). "In flight" = `in_production` +
 * `in_transit` (still actively moving); "total" = the sum of all 4
 * pipeline-bucket counts (same draft/cancelled exclusion
 * `computePipelineTotals` already applies — this is a pure re-fold of its
 * output, not a second pass over `orders`). Verified against the handoff's
 * worked example: bucket counts 4/3/2/4 → "7 in flight · 13 total".
 */
export function computePipelineSummary(totals: PipelineTotals): {
  readonly inFlight: number
  readonly total: number
} {
  const total = PIPELINE_BUCKETS.reduce((sum, bucket) => sum + totals[bucket].count, 0)
  const inFlight = totals.in_production.count + totals.in_transit.count
  return { inFlight, total }
}

export interface SpendSummary {
  readonly total: number
  readonly count: number
  readonly avg: number
  readonly currency: string
}

/**
 * ADR-0036 §5 "trivial trio": total spend, order count, average order value —
 * all a simple fold over the already-fetched `OrderSummary[]`. Cancelled
 * orders are excluded ("cancelled spend isn't spend", UX spec §1.6 item 4);
 * draft orders (not yet placed) are excluded the same way. Currency is taken
 * from the first counted order (demo assumption: single currency per
 * account, per ADR-0036 §1); defaults to "USD" when the list is empty so
 * callers never have to special-case an empty currency string.
 */
export function computeSpendSummary(orders: readonly OrderSummary[]): SpendSummary {
  const counted = orders.filter((order) => order.status !== 'draft' && order.status !== 'cancelled')
  const total = counted.reduce((sum, order) => sum + Number(order.total.amount), 0)
  const count = counted.length
  const currency = counted[0]?.total.currency ?? 'USD'
  return {
    total,
    count,
    avg: count === 0 ? 0 : total / count,
    currency,
  }
}

/**
 * Lowercase period phrase for the Spend card subline ("… · last 90 days"),
 * matching the handoff's own `presetPhrase` map exactly (`Fictiv Platform.dc.html`
 * ~L7803: `{'30d':'last 30 days','90d':'last 90 days','ytd':'year to date',
 * 'lastyear':'last year','all':'all time'}`). Purely a display-string lookup
 * for `OrdersListPage`'s spend date-range preset id — no data implication. A
 * custom (non-preset) calendar range falls back to a computed "MMM D – MMM D,
 * YYYY" phrase instead of a lookup (S5/UID-99, O4).
 */
export const SPEND_PERIOD_PHRASE: Record<string, string> = {
  all: 'all time',
  last30: 'last 30 days',
  last90: 'last 90 days',
  ytd: 'year to date',
  lastyear: 'last year',
}

/**
 * Formats a CUSTOM (non-preset) spend date-range phrase for the Spend card
 * subline (O4/UID-99, Gate 5 review fix). Same-year ranges read
 * unambiguously with a single trailing year ("Dec 1 – Dec 15, 2026"); a
 * range that SPANS a calendar-year boundary (e.g. Dec 15, 2026 – Jan 3,
 * 2027) explicitly repeats the year on the "from" side too — a bare
 * "Dec 15 – Jan 3, 2027" would otherwise read as if both ends fell in 2027.
 */
export function formatSpendRangePhrase(range: {
  readonly from: Date
  readonly to: Date
}): string {
  const short = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const spansYears = range.from.getFullYear() !== range.to.getFullYear()
  const fromLabel = spansYears
    ? `${short(range.from)}, ${range.from.getFullYear()}`
    : short(range.from)
  return `${fromLabel} – ${short(range.to)}, ${range.to.getFullYear()}`
}

export function formatMoney(amount: number, currency: string): string {
  // UID-317: the runtime/browser locale (`undefined`) can render USD as
  // "US$" instead of the design's bare "$" (Fictiv Platform.dc.html shows
  // e.g. "$1,284.00"). Pin the locale to 'en-US' (matching the other two
  // `formatMoney` helpers in this codebase) and force `currencyDisplay:
  // 'narrowSymbol'` so USD is guaranteed to render as "$" even if the
  // locale ever changes.
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
    maximumFractionDigits: 2,
  }).format(amount)
}

/**
 * The documented `Order` total rule (ADR-0029): `Σ(line.price.amount ×
 * line.quantity.value) + Σ(charge.amount.amount)`. The detail screen's `Order`
 * has no `total` field (unlike the list's `OrderSummary`, which the BFF
 * pre-computes via the shared `toOrderSummary`) — so this DELEGATES to the ONE
 * canonical total rule in `@misumi/shared` (`computeOrderTotal`, the same helper
 * `toOrderSummary` uses), guaranteeing the detail total is computed by the
 * identical integer-cents logic as the list total and can never diverge from it
 * (ADR-0036 §1). No float math lives here anymore. The shared helper returns the
 * exact 2-decimal money string; this adapts it to the `number` the detail card's
 * `formatMoney` consumes, preserving this function's public shape.
 */
export function computeOrderTotal(order: Pick<Order, 'lines' | 'charges'>): {
  readonly amount: number
  readonly currency: string
} {
  const { amount, currency } = computeOrderTotalMoney(order)
  return { amount: Number(amount), currency }
}

/** A bare `YYYY-MM-DD`, i.e. an OpenAPI `format: date` value. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export function formatDate(isoDate: string | null | undefined): string {
  if (!isoDate) return '—'
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    // This function takes both kinds of contract date, and they must not be
    // rendered the same way. A `format: date` value (estShipDate,
    // estDeliveryDate) is a CALENDAR date with no time or zone, but JS parses
    // '2026-02-10' as UTC midnight — so rendering it in the viewer's local zone
    // shows the previous day at every UTC-negative offset, i.e. a one-day-early
    // est-ship date for every user in the Americas. Pin those to UTC so the
    // calendar date survives the round trip. A `format: date-time` value
    // (deliveredAt, shippedAt, placedAt) IS an instant, so it keeps rendering in
    // the viewer's own zone, which is what a reader of an event timestamp wants.
    // Checkout's own formatEstDelivery already guards the same hazard the other
    // way round (it parses `${date}T00:00` as LOCAL midnight); pinning the
    // formatter is equivalent for date-only input and, unlike that trick, leaves
    // the date-time branch of this shared helper untouched.
    ...(DATE_ONLY_PATTERN.test(isoDate) ? { timeZone: 'UTC' } : {}),
  })
}

// ---------------------------------------------------------------------------
// UID-121 (S7) — order activity log + Customs info display mappings
// ---------------------------------------------------------------------------

/** Same as `formatDate` but includes the time-of-day (UX: activity-log
 * entries are timestamped events, not just calendar dates). */
export function formatDateTime(isoDate: string | null | undefined): string {
  if (!isoDate) return '—'
  const parsed = new Date(isoDate)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** Human-readable label for `ActivityEntry.kind` (UID-121 S7). */
export const ACTIVITY_KIND_LABEL: Record<ActivityEntry['kind'], string> = {
  created: 'Created',
  status_changed: 'Status changed',
  placed: 'Placed',
  in_production: 'In production',
  shipped: 'Shipped',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  field_updated: 'Field updated',
  po_updated: 'PO updated',
  address_updated: 'Address updated',
  customs_submitted: 'Customs submitted',
  document_added: 'Document added',
  submitted: 'Submitted',
  note: 'Note',
}

/** Human-readable label for `ActivityEntry.actorKind` (UID-121 S7). */
export const ACTIVITY_ACTOR_KIND_LABEL: Record<NonNullable<ActivityEntry['actorKind']>, string> = {
  customer: 'Customer',
  fictiv: 'Fictiv',
  system: 'System',
}

/**
 * Resolve the order-detail "Order paid by" display name (UID-130 follow-up). The frozen `Order`
 * contract deliberately carries no payer/buyer name field (PaymentSelection is PCI-bounded and the
 * shipping snapshot drops the contact name), so the buyer identity is read from the one place it
 * legitimately lives on the aggregate: the `actor` of the customer-authored `placed` entry in the
 * real backend `activityLog`. Returns `null` when there is no such entry or it carries no actor —
 * the caller renders the same graceful "—" every other unpopulated summary field uses.
 */
export function resolveOrderPaidBy(order: Pick<Order, 'activityLog'>): string | null {
  const placed = (order.activityLog ?? []).find(
    (entry) => entry.kind === 'placed' && entry.subjectType === 'order' && entry.actor,
  )
  return placed?.actor ?? null
}

/** Human-readable label for `CustomsInfo.classification` (UID-121 S7),
 * mirroring the prototype's "Prototype"/"Production" copy. */
export const CUSTOMS_CLASSIFICATION_LABEL: Record<CustomsInfo['classification'], string> = {
  prototype: 'Prototype',
  production: 'Production',
}

/** Descriptive copy under the classification heading (prototype's
 * `customsClassDesc`, UID-121 S7). */
export const CUSTOMS_CLASSIFICATION_DESCRIPTION: Record<CustomsInfo['classification'], string> = {
  prototype:
    'Used for development, testing, or evaluation — not incorporated into a final commercial product for sale or distribution.',
  production: 'Incorporated into a final commercial product for sale or distribution.',
}

// ---------------------------------------------------------------------------
// S5 (UID-99) — shared ORDER-identity / PARTS / EST. DELIVERY view-model.
// The orders-list table (OrderTable) and the Home "Recent orders" widget
// (RecentOrders) both render the SAME `OrderSummary` fields (`orderName`,
// `partNames`, `estDeliveryDate` — B1/PR#105) and must never diverge on how
// they're formatted, so both screens call through these three helpers
// instead of each re-deriving their own text.
// ---------------------------------------------------------------------------

export interface OrderIdentity {
  /** The canonical "SO-…" reference (`formatOrderRef`), always present. */
  readonly ref: string
  /** The synthesized human order name; `null` when no line carries a label
   * — callers fall back to {@link OrderIdentity.ref} alone (never duplicate
   * the ref as a fake name). */
  readonly name: string | null
}

/**
 * Resolves an order's display identity (ORDER cell / "Recent orders" ORDER
 * NAME cell). `orderName` is optional/nullable on the wire (server-derived,
 * B1) — absent and `null` are treated identically.
 *
 * The two callers deliberately use these fields in OPPOSITE primary/
 * secondary roles — verified directly against the prototype's two distinct
 * row bindings, not a paraphrase of the ticket:
 *   - `OrderTable` (standalone `/orders` list, the `ordersRows` binding,
 *     `Fictiv Platform.dc.html` ~L3629-3651): the SO REF (`identity.ref`) is
 *     the PRIMARY line; the order name, when present, is the plain secondary
 *     caption underneath. This matches UID-130's original SO-ref-primary
 *     decision for this screen. A later UID-132 pass incorrectly "reversed"
 *     this to name-primary based on a misreading of the ticket that
 *     conflated this screen with the Home widget below; that reversal has
 *     been undone.
 *   - `RecentOrders` (Home "Recent orders" widget, the `ordersTable`
 *     binding, ~L452-488): the order NAME is primary
 *     (`identity.name ?? identity.ref`), with
 *     {@link formatOrderIdentitySubtitle}'s "SO-##### · Qty N" as the
 *     secondary caption underneath. This is the correct, unchanged UID-132
 *     behavior — it applies to the Home widget only, never to `OrderTable`.
 * Both screens still call through this ONE helper so the underlying ref/name
 * resolution can't drift — only which field each screen treats as "primary"
 * differs, in each component's own render, not here.
 */
export function resolveOrderIdentity(order: Pick<OrderSummary, 'id' | 'orderName'>): OrderIdentity {
  return { ref: formatOrderRef(order.id), name: order.orderName ?? null }
}

/**
 * `RecentOrders`-only ORDER cell subtitle: "SO-##### · Qty N", underneath the
 * primary order-name line (`Fictiv Platform.dc.html` home `ordersTable`
 * ~L457: `{{ o.id }} · Qty {{ o.qty }}`). Always rendered — unlike the
 * primary line, the subtitle has no null case (the SO ref and unit count are
 * both always present on an `OrderSummary`). NOT used by `OrderTable`'s
 * standalone `/orders` list — that screen's secondary line is simply the
 * order name itself (or nothing, when absent); see `resolveOrderIdentity`'s
 * doc comment for the two screens' opposite primary/secondary roles.
 */
export function formatOrderIdentitySubtitle(order: Pick<OrderSummary, 'id' | 'unitCount'>): string {
  return `${formatOrderRef(order.id)} · Qty ${order.unitCount}`
}

/**
 * PARTS cell: "<count> part(s) · <first part name, second part name, …>".
 * `partNames` is optional/nullable on the wire; empty/absent falls back to
 * just the count (no dangling " · ").
 */
/**
 * PARTS cell — top line (`Fictiv Platform.dc.html` `ordersRows` ~L3641):
 * literally `{partCountLabel} · {qty} units`, e.g. "2 parts · 24 units". The
 * part NAMES are a SEPARATE bold line below ({@link formatPartNames}), not
 * folded into this summary — an earlier pass rendered "N parts · <names>" on
 * one line, which the design does not.
 */
export function formatPartsSummary(order: Pick<OrderSummary, 'lineCount' | 'unitCount'>): string {
  const countLabel = `${order.lineCount} part${order.lineCount === 1 ? '' : 's'}`
  return `${countLabel} · ${order.unitCount} unit${order.unitCount === 1 ? '' : 's'}`
}

/**
 * PARTS cell — the second, bold line (`ordersRows` ~L3643 `{{ partNames }}`):
 * the order's part names joined for display, or `null` when none are known
 * (the caller omits the line entirely rather than rendering an empty row).
 */
export function formatPartNames(order: Pick<OrderSummary, 'partNames'>): string | null {
  const names = (order.partNames ?? []).filter((name) => name.trim().length > 0)
  return names.length > 0 ? names.join(', ') : null
}

/**
 * UID-317 review follow-up: the orders-list PARTS cell shows the first part name (itself
 * ellipsis-truncated if long) plus a "+N more" count for the remainder, instead of a single
 * comma-joined string cut mid-word by the CSS ellipsis. Returns the first name + how many more
 * parts the order has, or `null` when no names are known.
 */
export function resolvePartNamesDisplay(
  order: Pick<OrderSummary, 'partNames'>,
): { readonly primary: string; readonly moreCount: number } | null {
  const names = (order.partNames ?? []).filter((name) => name.trim().length > 0)
  const primary = names[0]
  if (primary === undefined) return null
  return { primary, moreCount: names.length - 1 }
}

/**
 * EST. DELIVERY cell: reuses {@link formatDate} (same em-dash-for-null
 * convention as every other nullable date in this vertical) over the
 * server-rolled-up `estDeliveryDate` (optional/nullable on the wire).
 */
export function formatEstDelivery(order: Pick<OrderSummary, 'estDeliveryDate'>): string {
  return formatDate(order.estDeliveryDate)
}

/**
 * PAYMENT TYPE cell (UID-132) — `RecentOrders`-only (the Home widget's
 * `ordersTable` binding genuinely has this column). The standalone `/orders`
 * list (`OrderTable`, the `ordersRows` binding) has NO Payment-type column
 * in the design at all; an earlier UID-132 pass incorrectly added one there
 * too, since removed. Renders the server-derived `paymentDisplayLabel`
 * (`@misumi/shared`'s `derivePaymentDisplayLabel`, projected from the order's
 * frozen `payment` snapshot) as-is, or the same "—" every other unpopulated
 * summary field uses when the order carries no payment snapshot. A genuine,
 * always-available field once `payment` is present — NOT the "Project" gap
 * below (there is no backing data model for that one).
 */
export function formatPaymentType(order: Pick<OrderSummary, 'paymentDisplayLabel'>): string {
  return order.paymentDisplayLabel ?? '—'
}

/**
 * ---------------------------------------------------------------------------
 * PROJECT column — DISCLOSED, GENUINE CONTRACT GAP (UID-132, not blocking).
 * ---------------------------------------------------------------------------
 * The design's ORDER row (`Fictiv Platform.dc.html` home `ordersTable`
 * ~L460-462) shows a "Project" column: either the associated workspace's
 * name, or a "+ Add project" affordance when none is assigned
 * (`o.hasWs`/`o.notHasWs`/`o.onAddWs`). Workspaces are a REAL, shipped
 * vertical (UID-104, `/projects/*`) — but there is NO `workspaceId` /
 * `projectId` field anywhere on `Order` or `OrderSummary`, and no join table
 * linking an order to a workspace, anywhere in the contract. This is not an
 * oversight to quietly patch around; it's the same category of gap this
 * codebase has already disclosed rather than fabricated (see
 * `OrderSummaryCard.tsx`'s "Reference quote #" — "None", no quote id exists
 * on the Order contract — and UID-130's now-fixed "Order paid by").
 *
 * Per the ticket's explicit instruction, the design's "+ Add project"
 * affordance is rendered for PIXEL fidelity — it looks and behaves like a
 * real clickable element — but it is INERT: clicking it shows an
 * informational toast ("Assigning orders to a project isn't available
 * yet.") rather than calling any endpoint, because none exists. Only
 * `RecentOrders` (the Home widget's `ordersTable` binding, which genuinely
 * has a Project column) renders it, via the shared `AddProjectAffordance`
 * component (kept UI-only, not here, since this module stays React-free).
 * The standalone `/orders` list (`OrderTable`, the `ordersRows` binding) has
 * NO Project column in the design at all — an earlier UID-132 pass
 * incorrectly added one there too; that column has been removed.
 * `AddProjectAffordance` remains a real, in-use component — only its
 * `OrderTable` call site was removed, not the component itself. A future
 * ticket should add a real workspace↔order association (a join table or an
 * `Order.workspaceId`) before this can do anything real.
 * ---------------------------------------------------------------------------
 */

/**
 * The orders-list search predicate (UX spec: "Search by ID, name, or part",
 * O1/UID-99, Gate 5 review fix). `query` is already trimmed/lower-cased by
 * the caller; an empty query always matches (no filter applied).
 *
 * Matches, in order: the visible SO ref ({@link formatOrderRef}) — the
 * user-facing "ID" this screen's ORDER cell now displays since O1, so a
 * search for what's literally on screen must hit; the RAW `order.id` UUID
 * too, belt-and-suspenders, since some users paste a raw id copied from an
 * API response and that match was already free before O1; `poReference`;
 * `orderName`; and every entry in `partNames`.
 */
export function matchesOrderSearch(
  order: Pick<OrderSummary, 'id' | 'poReference' | 'orderName' | 'partNames'>,
  query: string,
): boolean {
  if (!query) return true
  return (
    formatOrderRef(order.id).toLowerCase().includes(query) ||
    order.id.toLowerCase().includes(query) ||
    (order.poReference?.toLowerCase().includes(query) ?? false) ||
    (order.orderName?.toLowerCase().includes(query) ?? false) ||
    (order.partNames ?? []).some((name) => name.toLowerCase().includes(query))
  )
}

// ---------------------------------------------------------------------------
// §2.4 — per-line OrderLineStatus → mini-stepper / chip UI mapping
// ---------------------------------------------------------------------------

export type LineStage = 1 | 2 | 3 | 4

export interface LineStatusUiMeta {
  readonly label: string
  readonly tone: ChipTone
  readonly iconKey: IconKey
  /** `null` for `cancelled` — UX spec §2.4: cancelled is not a stage on the
   * linear mini-stepper at all, it replaces the stepper with a single muted
   * chip. */
  readonly stage: LineStage | null
  /** Tracking link becomes available once a line has shipped (§2.4) — real
   * carrier/tracking-number data does not exist in the contract yet (§2.7,
   * flagged to architect); this only signals WHEN the UI should attempt to
   * show a tracking affordance once that data exists. */
  readonly trackingAvailable: boolean
}

export const ORDER_LINE_STATUS_UI: Record<OrderLineStatus, LineStatusUiMeta> = {
  placed: {
    label: 'Placed',
    tone: 'info',
    iconKey: 'check-circle',
    stage: 1,
    trackingAvailable: false,
  },
  in_production: {
    label: 'In production',
    tone: 'info',
    iconKey: 'setting',
    stage: 2,
    trackingAvailable: false,
  },
  shipped: {
    label: 'Shipped',
    tone: 'info',
    iconKey: 'truck',
    stage: 3,
    trackingAvailable: true,
  },
  delivered: {
    label: 'Delivered',
    tone: 'success',
    iconKey: 'check-circle-filled',
    stage: 4,
    trackingAvailable: true,
  },
  cancelled: {
    label: 'Cancelled',
    tone: 'neutral',
    iconKey: 'close-circle',
    stage: null,
    trackingAvailable: false,
  },
}

// ---------------------------------------------------------------------------
// UID-123 (S8 design-fidelity round 2, item 4) — Shipment.status → status-pill
// UI mapping, the per-shipment-card equivalent of ORDER_LINE_STATUS_UI above.
// ---------------------------------------------------------------------------

export const SHIPMENT_STATUS_UI: Record<Shipment['status'], StatusChipMeta> = {
  // UID-212 follow-up: tones match the prototype's shipment-status pill palette
  // (`Fictiv Platform.dc.html:9807` shipHeadPill + `:9648` P2, whose exact fg/bg/border hexes are
  // the --color-neutral / --color-warning (#F0811E) / --color-success (#16BC9C) tokens): preparing
  // = neutral gray, in production = orange, shipped/in transit/delivered = green. NOT the earlier
  // blue (`info`).
  //
  // `pending` shows "Preparing" on the UI (the design's pill copy) — a DISPLAY LABEL ONLY; the
  // underlying Shipment.status stays `pending` everywhere else (API, persistence, the
  // "NOT YET SHIPPED" status stack).
  pending: { label: 'Preparing', tone: 'neutral', iconKey: 'clock-circle' },
  in_production: { label: 'In production', tone: 'warning', iconKey: 'setting' },
  shipped: { label: 'Shipped', tone: 'success', iconKey: 'truck' },
  in_transit: { label: 'In transit', tone: 'success', iconKey: 'truck' },
  delivered: { label: 'Delivered', tone: 'success', iconKey: 'check-circle-filled' },
  cancelled: { label: 'Cancelled', tone: 'neutral', iconKey: 'close-circle' },
}

/**
 * The date the Shipments-tab card leads with as its big headline value
 * (`Fictiv Platform.dc.html` ~L3714-3715, `deliveredLabel`): once delivered,
 * the ACTUAL delivery date; otherwise the estimated ship date. Returns the
 * raw ISO value (or `null`) — the caller runs it through {@link formatDate}
 * so the "—" fallback stays consistent with the rest of the screen.
 *
 * Code-review tightening: the `delivered` branch does NOT fall back to
 * `estShipDate` when `deliveredAt` is missing — showing an ESTIMATED ship
 * date under a "Delivered" label would be misleading. A delivered shipment
 * with no recorded delivery date renders "—" instead.
 */
export function resolveShipmentHeadlineDate(shipment: Shipment): string | null {
  return shipment.status === 'delivered'
    ? (shipment.deliveredAt ?? null)
    : (shipment.estShipDate ?? null)
}

/**
 * Derive the Shipments-tab "Shipping status" cell's stacked lines
 * (`Fictiv Platform.dc.html` ~L3723 / L9006-9009 `shipStatusLines`): an
 * ALL-CAPS headline, a sub-status, then an est/actual date line. The
 * prototype hard-codes these from a demo step index; here they are derived
 * from the REAL nullable `Shipment` fields (status/carrier/dates/signedBy),
 * dropping any line the data can't back rather than inventing copy. Pure and
 * unit-testable — no rendering. Dates are ISO in, {@link formatDate}'d here.
 */
export function deriveShipmentStatusLines(shipment: Shipment): string[] {
  const est = shipment.estShipDate ? `Est. ship ${formatDate(shipment.estShipDate)}` : null

  switch (shipment.status) {
    case 'delivered': {
      const lines = ['DELIVERED']
      if (shipment.signedBy) lines.push(`Signed by ${shipment.signedBy}`)
      if (shipment.deliveredAt) lines.push(`Delivered ${formatDate(shipment.deliveredAt)}`)
      return lines
    }
    case 'shipped':
    case 'in_transit': {
      const headline = shipment.status === 'shipped' ? 'SHIPPED' : 'IN TRANSIT'
      const lines = [headline]
      lines.push(shipment.carrier ? `On ${shipment.carrier} network` : 'On its way')
      if (shipment.shippedAt) lines.push(`Shipped ${formatDate(shipment.shippedAt)}`)
      else if (est) lines.push(est)
      return lines
    }
    case 'cancelled':
      return ['CANCELLED']
    default: {
      // `pending` / `in_production`: not yet shipped. Code-review: the
      // sub-line tracks the status pill so the two don't read as
      // contradictory — an `in_production` shipment says "In production"
      // (matching SHIPMENT_STATUS_UI's pill), not "Awaiting production".
      const lines = ['NOT YET SHIPPED']
      lines.push(shipment.status === 'in_production' ? 'In production' : 'Awaiting production')
      if (est) lines.push(est)
      return lines
    }
  }
}

/**
 * Resolve the Shipments-tab card's "Shipping method" field (UID-224).
 * Descoped from a lifecycle/reconciler feature to a pure display + fallback
 * concern: every placed order carries exactly one `pending` "Shipment 1",
 * and own-account orders get their carrier/account snapshot frozen onto
 * `order.shippingMethod` (already masked to `••last4` server-side, UID-206
 * — this never re-masks). Own-account renders `Own account · <carrier>
 * <accountRef>`; when the snapshot carries no carrier/accountRef detail it
 * falls back to the shipment's `serviceLevel`, then the bare `'Own account'`
 * label. Non-own-account (prepay) orders render exactly as before —
 * `shipment.serviceLevel` (or `null`, the caller's existing "—" fallback).
 *
 * Retrieval fallback: when `order.shippingMethod` is absent (e.g. an older
 * snapshot or a projection that dropped it), reconstruct the same string
 * from the masked own-account activity-log note the backend writes at
 * placement (`"Shipping method: Own account · <carrier> ••<last4>"`) —
 * returning everything after `"Shipping method: "` so it matches the normal
 * path's format exactly. Falls through to `shipment.serviceLevel ?? null`
 * when neither `shippingMethod` nor a matching note exists.
 */
export function formatShipmentShippingMethod(order: Order, shipment: Shipment): string | null {
  const method = order.shippingMethod
  if (method?.kind === 'own_account') {
    // accountRef is already masked server-side (UID-206 `toReadOrder`); the shared formatter keeps
    // this line byte-identical to the placement activity-log note so the two can't drift (W2/S3).
    const line = formatOwnAccountShippingLine({
      carrier: method.carrier,
      maskedAccountRef: method.accountRef,
    })
    return line === 'Own account' ? (shipment.serviceLevel ?? 'Own account') : line
  }
  if (!method) {
    // Retrieval fallback: reconstruct from the masked own-account note the backend writes at
    // placement. Scoped to the system-written `note` kind (not any message) so an unrelated entry
    // can't be mistaken for it (code-review S2).
    const note = (order.activityLog ?? []).find(
      (entry) =>
        entry.kind === 'note' &&
        entry.actorKind === 'system' &&
        entry.message.startsWith(SHIPPING_METHOD_NOTE_PREFIX),
    )
    if (note) return note.message.slice(SHIPPING_METHOD_NOTE_PREFIX.length)
  }
  // UID-317 review follow-up: for a PREPAY (Fictiv-arranged) method, fall back to the tier the
  // customer chose at checkout (`order.shippingMethod.serviceLevel`) when this shipment carries no
  // service level of its own — otherwise a pending/synthesized prepay shipment showed a bare "—"
  // even though the order records the selected tier. Still null (→ "—") when neither has one.
  return shipment.serviceLevel ?? (method?.kind === 'prepay' ? (method.serviceLevel ?? null) : null)
}

/**
 * UX spec §2.5: `partially_shipped` renders as the Shipped stage in a
 * partial-fill state, with a "N of M shipped" fraction — counting lines that
 * have reached `shipped` or `delivered` against the total non-cancelled line
 * count. Returns `null` when the order isn't `partially_shipped` (nothing to
 * render).
 */
export function computeShippedFraction(
  order: Pick<Order, 'status' | 'lines'>,
): { readonly shipped: number; readonly total: number } | null {
  if (order.status !== 'partially_shipped') return null
  const countable = order.lines.filter((line) => line.status !== 'cancelled')
  const shipped = countable.filter(
    (line) => line.status === 'shipped' || line.status === 'delivered',
  ).length
  return { shipped, total: countable.length }
}

// ---------------------------------------------------------------------------
// §2.2/§2.3 — order-level status stepper stage resolution
// ---------------------------------------------------------------------------

export type OrderStepId = 'placed' | 'in_production' | 'shipped' | 'delivered'

export const ORDER_STEPS: readonly { readonly id: OrderStepId; readonly label: string }[] = [
  { id: 'placed', label: 'Order received' },
  { id: 'in_production', label: 'Production pending' },
  { id: 'shipped', label: 'Shipped' },
  { id: 'delivered', label: 'Delivered' },
]

export type StepState = 'completed' | 'current' | 'upcoming'

/**
 * Maps an order onto the 4-step linear stepper (UX spec §2.2 item 3 / §2.3 /
 * §2.5). `on_hold` and `cancelled` are NOT steps of their own — they render
 * as an overlay banner above the stepper (§2.4) — so this resolves the
 * stepper's UNDERLYING progress the hold/cancellation interrupted.
 *
 * `on_hold` has no dedicated stage field in the contract (the §2.4-documented
 * asymmetry: `OrderLineStatus` carries no `on_hold` value either), so the
 * "stage the order was at" is inferred from the most-advanced non-cancelled
 * line's status — a documented approximation, not a backend fact.
 * `cancelled` returns `null` (no step is "current"; the banner is the whole
 * story). `draft` also returns `null` (the detail screen should never render
 * a draft order in practice — orders are always placed by contract — but
 * this keeps the function total rather than throwing).
 */
export function resolveOrderStepState(order: Pick<Order, 'status' | 'lines'>): {
  readonly currentIndex: number | null
  readonly interrupted: 'on_hold' | 'cancelled' | null
} {
  switch (order.status) {
    case 'placed':
      return { currentIndex: 0, interrupted: null }
    case 'in_production':
      return { currentIndex: 1, interrupted: null }
    case 'partially_shipped':
    case 'shipped':
      return { currentIndex: 2, interrupted: null }
    case 'delivered':
    case 'closed':
      return { currentIndex: 3, interrupted: null }
    case 'cancelled':
      return { currentIndex: mostAdvancedLineStage(order.lines), interrupted: 'cancelled' }
    case 'on_hold':
      return { currentIndex: mostAdvancedLineStage(order.lines), interrupted: 'on_hold' }
    default:
      // 'draft' falls through here — the detail screen should never render a
      // draft order in practice (orders are always placed by contract), but
      // this keeps the function total rather than throwing.
      return { currentIndex: null, interrupted: null }
  }
}

function mostAdvancedLineStage(lines: readonly OrderLine[]): number | null {
  let best: number | null = null
  for (const line of lines) {
    const stage = ORDER_LINE_STATUS_UI[line.status].stage
    if (stage === null) continue
    // OrderStepId indices: placed=0, in_production=1, shipped=2, delivered=3.
    // LineStage 1..4 maps 1:1 onto those same 4 stages.
    const stepIndex = stage - 1
    if (best === null || stepIndex > best) best = stepIndex
  }
  return best
}

export function stepState(stepIndex: number, currentIndex: number | null): StepState {
  if (currentIndex === null) return 'upcoming'
  if (stepIndex < currentIndex) return 'completed'
  if (stepIndex === currentIndex) return 'current'
  return 'upcoming'
}

// ---------------------------------------------------------------------------
// S6/UID-100 D3 (documents) / D4 (action bar) — shared safe-reference check.
// `OrderDocument.reference` is an UNTRUSTED string pointing at a seeded demo
// fixture (never generated server-side) — both ShipmentsPanel and
// ActionBar (parts-and-drawings / order-confirmation) must refuse to render
// it as a clickable `href` unless it is an absolute http(s) URL or a
// same-origin relative path. ONE home for the check so it can't drift
// between the two call sites.
// ---------------------------------------------------------------------------

/**
 * Returns `true` only for references safe to place into an `href`: a genuinely
 * absolute `http://`/`https://` URL (may be external — the sinks harden
 * external links with `target="_blank" rel="noopener noreferrer"`), or a
 * genuine SAME-ORIGIN rooted path. Rejects `javascript:`, `data:`,
 * `vbscript:`, and any other scheme, protocol-relative `//host/...`, and
 * empty/malformed input.
 *
 * SECURITY (UID-100 follow-up): the classification is done by RESOLVING the
 * reference against our own origin and comparing origins — NOT by matching the
 * raw string's surface shape. The old `startsWith('/') && !startsWith('//')`
 * guard trusted a leading `/` as proof of same-origin, but WHATWG URL parsing
 * folds a backslash to a forward slash, so `/\evil.com`, `/\/evil.com`, and
 * `/\@evil.com` all LOOK same-origin-relative yet resolve to
 * `http(s)://evil.com/` — off-origin. Being mis-classified "internal", they
 * skipped the external-link hardening in the sinks. Resolve-and-compare closes
 * that bypass: any reference that isn't an explicitly-absolute http(s) URL
 * must resolve back to our own origin, which the `\` and `//` tricks never do.
 */
export function isSafeDocumentReference(reference: string): boolean {
  if (typeof reference !== 'string' || reference.length === 0) return false
  let url: URL
  try {
    url = new URL(reference, window.location.origin)
  } catch {
    return false
  }
  // Only real web schemes — rejects javascript:, data:, vbscript:, mailto:, …
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
  // An explicitly-absolute http(s) reference is allowed (it may legitimately
  // be external; the caller classifies + hardens external links). Matched on
  // the RAW string so a rooted/relative reference can never masquerade as one.
  if (/^https?:\/\//i.test(reference)) return true
  // Otherwise it must be a genuine same-origin rooted path. Requiring BOTH a
  // leading "/" AND a same-origin resolution rejects the backslash tricks and
  // protocol-relative `//host` (both resolve off-origin) and bare tokens like
  // "not a url" (not rooted). This preserves the same-origin/external contract
  // the sinks (ShipmentsPanel, ActionBar) depend on for link hardening.
  return reference.startsWith('/') && url.origin === window.location.origin
}

// ---------------------------------------------------------------------------
// S6/UID-100 D3/D5 — shared per-line DISPLAY-LABEL preference. The per-line
// tracking table, the shipments panel, and the documents section all need the
// same one-line label for an OrderLine; ONE home here stops them drifting to
// three different fallbacks (they previously each picked their own).
// ---------------------------------------------------------------------------

/**
 * The single per-line label preference: the as-placed friendly `displayName`,
 * else the catalog `partNumber`, else `sourceable.id` — a stable, meaningful
 * part identifier, deliberately NOT the opaque order-line UUID (`line.id`).
 * QA (UID-100) confirmed no real, labeled line reaches the final fallback
 * today; it exists only so the label is never blank.
 */
export function resolveLineLabel(
  line: Pick<OrderLine, 'displayName' | 'partNumber' | 'sourceable'>,
): string {
  return line.displayName ?? line.partNumber ?? line.sourceable.id
}

// ---------------------------------------------------------------------------
// UID-123 (S8 design-fidelity round 2, Gate-5 review fix) — per-line status
// fallback (ShipmentsPanel) helpers.
// ---------------------------------------------------------------------------

/**
 * The per-line "delivery promise date" shown by the {@link
 * resolveLineLabel}-adjacent per-line fallback list: the LAST entry in
 * `deliveryPromise.committedDates` (ADR-0004's committed, not actual, dates
 * — `committedDates` may hold more than one entry for a split-shipment
 * schedule, and the last one is the line's overall completion promise).
 * `null` when no commitment exists yet, so callers can format it through
 * {@link formatDate}'s existing null-safe "—" convention rather than
 * inventing a second one here.
 */
export function resolveLineDeliveryDate(line: Pick<OrderLine, 'deliveryPromise'>): string | null {
  const dates = line.deliveryPromise.committedDates
  return dates.length > 0 ? (dates[dates.length - 1] ?? null) : null
}

/**
 * UID-317 review follow-up: the est-ship date a "Preparing" shipment shows must
 * match the value the CHECKOUT page displayed — i.e. the shipping method's
 * `estDeliveryDate` snapshot carried on the order's `shippingMethod` (only the
 * `prepay` method kind carries one; `own_account` has none). Read it straight
 * from the contract rather than re-deriving a date here, so the Orders detail
 * page never disagrees with what the customer saw at checkout. Returns null
 * when the order has no prepay shipping snapshot with a date.
 */
export function resolveContractEstShipDate(order: Pick<Order, 'shippingMethod'>): string | null {
  const method = order.shippingMethod
  return method && method.kind === 'prepay' ? (method.estDeliveryDate ?? null) : null
}

/**
 * UID-317 review follow-up — STUB. The system stores only a DELIVERY date (the shipping method's
 * `estDeliveryDate` / the line's committed date); there is no distinct est-SHIP date anywhere in
 * checkout, placement, or the contract. So the "Est. ship" surface would otherwise duplicate the
 * delivery date. Until a real ship-date source exists, estimate it as `delivery − transit`, keying
 * transit days off the (nullable) shipping service level. This is an intentional placeholder
 * formula (NOT a committed business promise) — swap it for a real ship-date field when one lands.
 * Returns null when there is no delivery date to work back from.
 */
const STUB_TRANSIT_DAYS_BY_SERVICE: Readonly<Record<string, number>> = {
  overnight: 1,
  'next day': 1,
  'priority overnight': 1,
  'one day': 1,
  express: 2,
  '2-day': 2,
  'two day': 2,
  'two-day': 2,
  expedited: 3,
  ground: 5,
  standard: 5,
}

export function estimateShipDateFromDelivery(
  deliveryDate: string | null,
  serviceLevel: string | null,
): string | null {
  if (!deliveryDate) return null
  const parsed = new Date(`${deliveryDate}T00:00:00Z`)
  if (Number.isNaN(parsed.getTime())) return null
  const transitDays = STUB_TRANSIT_DAYS_BY_SERVICE[(serviceLevel ?? '').trim().toLowerCase()] ?? 5
  parsed.setUTCDate(parsed.getUTCDate() - transitDays)
  return parsed.toISOString().slice(0, 10)
}
